/**
 * Characters — the system, the public `ctx.characters` API, and the shots.
 *
 * One soldier = ONE THREE.SkinnedMesh with four material groups
 * (skin / cloth_camo / webbing / gun_polymer), so a character is four draw calls
 * INCLUDING the carbine, which is rigid-skinned to the right hand. Geometry is cached
 * per distinct loadout shape, so a squad of twelve shares a handful of buffers.
 *
 * Each character also carries two reduced meshes from `Lod.js`, selected by distance
 * every frame (2 groups beyond 12 m, 1 group beyond 20 m). Four groups is four draws
 * in the colour pass AND in the geometry prepass AND in every shadow cascade — the
 * squad shot redraws each soldier 4.75 times — so the group count, not the triangle
 * count, is what a twelve-man squad actually costs. Measured on `char_squad`:
 * 228 character draw calls before, 138 after.
 *
 * Public API (frozen for the AI team — see the report):
 *
 *   ctx.characters.spawn(opts) -> character
 *   character.setLocomotion({ velocity, grounded, crouched, stance })
 *   character.setAimTarget(v3)   character.setLookTarget(v3)
 *   character.play(name, opts)   character.hit(region, impulse, point)
 *   character.die(impulse, boneName)
 *   character.getBone(name)      character.weaponAttach
 *   character.dispose()
 *
 * owner: characters
 */
import * as THREE from 'three';
import { registerShot, look } from '../core/Shots.js';
import { makeRng } from '../core/Rand.js';
import { createRig, buildRagdollSpec, RAGDOLL_DRIVE, REGION_BONE, REGION_RAGDOLL_BONE } from './Rig.js';
import { SkinBuilder, SLOT } from './Build.js';
import { buildBodyInto } from './Body.js';
import { buildGearInto } from './Gear.js';
import { buildLodInto } from './Lod.js';
import { makeLoadout, heroLoadouts } from './Loadout.js';
import { Animator } from './Anim.js';
import { Secondary } from './Secondary.js';

/**
 * The materials team exports a read handle onto the per-material shading uniforms.
 * A namespace import means a renamed/removed export degrades to "leave the defaults
 * alone" instead of failing at link time.
 */
import * as SurfaceRT from '../world/shaders/runtime.glsl.js';

const getSurfaceShading = typeof SurfaceRT.getSurfaceShading === 'function'
  ? SurfaceRT.getSurfaceShading : null;

/**
 * Where the showcase stage stands. This is open, flat ground: the whole 52 x 62 m
 * footprint measures level ground at y = -0.09 with **nothing at all within 120 m** in
 * any direction and nothing overhead.
 *
 * It used to be (0, 0, 186), and that site was described in this file as "clear of the
 * level". It stopped being clear. The level's outskirts district grew over it, and by the
 * time the content gate was written the stage was buried: measured at (0, 0, 186), the
 * level terrain rises to **y = 30.2 m across the stage footprint**, `khalij:concrete_wall`
 * sits **0.8 m** in front of the `char_squad` camera, and there is level geometry **0.23 m
 * overhead**. Every char shot was photographing the inside of a hill.
 *
 * That is the whole of the "NO CHARACTERS IN FRAME" defect on `char_squad`, all of the
 * "70 % occluded by an out-of-focus wall" on `char_variants`, and the "concrete soffit,
 * flat ambient, no key" note on `char_hero` — the soffit was a real building, 23 cm above
 * the soldier's helmet, and it was eating the sun.
 *
 * If you move this again, re-measure. A comment claiming a site is clear is a claim, not
 * a fact; `node tools/capture.mjs char_*` is the fact.
 */
const STAGE = new THREE.Vector3(-420, 0, -420);
const _v = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _euler = new THREE.Euler();
const UP_Y = new THREE.Vector3(0, 1, 0);

/**
 * The bones that describe a body's silhouette, for framing.
 *
 * Deliberately excludes `root` and the verlet-driven dynamic bones. `root` stays where
 * the character object is, which for a ragdoll is where he was STANDING — including it
 * stretched `char_ragdoll`'s framing box from a 1.1 m heap to a 3 m diagonal and pushed
 * the camera far enough back that the corpse measured 1.2 % of the frame. An antenna is
 * a wire; it should not decide how close the camera gets either.
 */
const BODY_BONES = [
  'hips', 'spine01', 'spine02', 'chest', 'neck', 'head', 'headEnd',
  'upperArmL', 'forearmL', 'handL', 'upperArmR', 'forearmR', 'handR',
  'thighL', 'shinL', 'footL', 'toeL', 'thighR', 'shinR', 'footR', 'toeR',
];

export function createCharacterSystem() {
  let ctxRef = null;
  const characters = [];
  const geoCache = new Map();
  const matCache = new Map();
  let stage = null;
  let shotCamActive = false;
  const shotCamPos = new THREE.Vector3();
  let idSeq = 1;

  /* ---------------------------------------------------------------- *
   * materials
   * ---------------------------------------------------------------- */
  function materialsFor(L) {
    const key = `${L.camoOffset[0].toFixed(3)},${L.camoOffset[1].toFixed(3)}`;
    let m = matCache.get(key);
    if (m) return m;
    const lib = ctxRef.materials;
    /**
     * `gain` lifts the material's base colour. The shared library is authored for a
     * pre-dawn map and `cloth_camo`/`webbing` sit around 0.05 linear — a soldier made
     * of them is a black cut-out. Multicam and coyote webbing are mid-value fabrics, so
     * the character multiplies the shared albedo up rather than shipping its own
     * textures (which would duplicate the materials team's work and their memory).
     */
    const mk = (name, opts, gain, fallback) => {
      let mat;
      if (lib && lib.make) {
        try { mat = lib.make(name, opts); } catch (e) { mat = null; }
      }
      if (!mat) mat = new THREE.MeshStandardMaterial(fallback);
      mat.vertexColors = true;
      mat.color.setRGB(gain[0], gain[1], gain[2]);
      mat.name = `char_${name}`;
      mat.needsUpdate = true;
      return mat;
    };
    // `worldSize` is metres per uv unit; our uvs are metric, so this IS the texture
    // scale on the body. Below 1 the pattern grows — camo blotches want to be 10-18 cm
    // across, and the library's fabrics are authored for a 0.55 m tile.
    m = [
      // gain leans COOL on skin: the library's skin is a warm subsurface preset built
      // for a hero head, and left alone every soldier is fluorescent orange
      // gain leans COOL and DOWN on skin: the library's preset is a warm subsurface
      // portrait head, and left alone every soldier is a fluorescent orange mannequin
      mk('skin', { worldSize: 0.55 }, [1.06, 1.16, 1.24],
        { color: 0xb08466, roughness: 0.72, metalness: 0 }),
      mk('cloth_camo', { worldSize: 1.15, offset: L.camoOffset }, [3.30, 3.15, 2.75],
        { color: 0x6d6a54, roughness: 0.95, metalness: 0 }),
      mk('webbing', { worldSize: 0.90, offset: [L.camoOffset[1], L.camoOffset[0]] }, [2.45, 2.25, 1.85],
        { color: 0x4a4a3c, roughness: 0.92, metalness: 0 }),
      mk('gun_polymer', { worldSize: 0.55 }, [1.70, 1.68, 1.62],
        { color: 0x2a2c2e, roughness: 0.6, metalness: 0.15 }),
    ];

    /**
     * NORMAL STRENGTH. The library's fabric normals are authored at architectural
     * amplitude. At half strength they still read as a basket weave at one metre — the
     * reviewer's "helmet made of burlap" and the black waffle across the plate carrier
     * in char_aim. A 600 g/m2 cordura weave is a ~1 mm feature: at any distance a game
     * ever shows a soldier it is a roughness variation, not a height field. So the
     * normals come down to a fifth and the missing tactility goes into roughness
     * instead, where it cannot cast a fake shadow.
     */
    m[0].normalScale.set(0.42, 0.42);
    m[1].normalScale.set(0.16, 0.16);
    m[2].normalScale.set(0.15, 0.15);
    m[3].normalScale.set(0.22, 0.22);

    // Skin: dial back the library's sheen and subsurface. Both are tuned for a close-up
    // portrait; on a helmeted soldier at three metres they just wash the face out — and
    // at full strength the face comes out fluorescent orange and waxy.
    if (m[0].sheen !== undefined) { m[0].sheen = 0.04; m[0].sheenColor?.setHex(0x6a4c40); }
    if (m[0].clearcoat !== undefined) m[0].clearcoat = 0.02;
    if (m[0].roughness !== undefined) m[0].roughness = Math.max(m[0].roughness, 0.66);

    // The shared library's self-detail is authored for architecture at 12x the base
    // tiling. On a 1 m-wide chest with metric uvs that lands at ~4 cm and reads as
    // chainmail. Retune it through the materials team's own exported handle — no fork,
    // no second texture set, and it degrades to a no-op if that export ever moves.
    // The tile the reviewer could find in every frame is this: a detail layer at ~4 cm
    // on a 1 m chest. Pushed to 9-14x the base it lands at 8-13 mm, which is under the
    // Nyquist limit of a soldier at 3 m and reads as fibre rather than as a lattice.
    const detail = [[9.0, 0.24, 0.07, 0.82], [11.0, 0.085, 0.035, 0.82],
      [12.0, 0.090, 0.040, 0.82], [14.0, 0.11, 0.045, 0.82]];
    const trans = [[0.055, 4.0, 0.60, 0.35], null, null, null];
    if (getSurfaceShading) {
      for (let i = 0; i < 4; i++) {
        const st = getSurfaceShading(m[i]);
        if (!st || !st.uniforms) continue;
        if (detail[i] && st.uniforms.uDetailP) st.uniforms.uDetailP.value.fromArray(detail[i]);
        if (trans[i] && st.uniforms.uTransP) st.uniforms.uTransP.value.fromArray(trans[i]);
      }
    }

    m[0].userData = { ...m[0].userData, surface: 'flesh' };
    m[1].userData = { ...m[1].userData, surface: 'fabric' };
    m[2].userData = { ...m[2].userData, surface: 'fabric' };
    m[3].userData = { ...m[3].userData, surface: 'metal' };
    matCache.set(key, m);
    return m;
  }

  /* ---------------------------------------------------------------- *
   * geometry
   * ---------------------------------------------------------------- */
  function shapeKey(L) {
    return [
      L.variant, L.scale.toFixed(3), L.helmet, L.helmetCover, L.nvgMount, L.railLights,
      L.goggles, L.boonie, L.beanie, L.headset, L.faceCover, L.shemagh, L.vestClass,
      L.magPouches, L.adminPouch, L.grenadePouch, L.radio, L.antenna, L.shoulderPads,
      L.kneePads, L.elbowPads, L.holster, L.dumpPouch, L.gloves, L.weapon,
      L.skin, L.uniform, L.vest, L.glove, L.boot, L.pad, L.hair,
      (L.girth ?? 1).toFixed(3), L.seed % 2,
    ].join('|');
  }

  function geometryFor(L, rig) {
    const key = shapeKey(L);
    const hit = geoCache.get(key);
    if (hit) return hit;
    const B = new SkinBuilder(rig);
    buildBodyInto(B, rig, L);
    const gear = buildGearInto(B, rig, L);
    const geometry = B.build();
    // Two reduced meshes for the distance LODs. They are cheap to author (see Lod.js)
    // and they are the only lever there is on draw calls, because three.js emits one
    // draw per material GROUP in the colour pass, the prepass and every shadow cascade.
    const lods = [1, 2].map((lvl) => {
      const Bl = new SkinBuilder(rig);
      buildLodInto(Bl, rig, L, lvl === 2);
      const g = Bl.build();
      return { geometry: g, tris: g.index.count / 3, draws: g.groups.filter((x) => x.count > 0).length };
    });
    const tris = geometry.index.count / 3;
    const draws = geometry.groups.filter((g) => g.count > 0).length;
    const entry = { geometry, gear, tris, draws, lods, refs: 0 };
    geoCache.set(key, entry);
    return entry;
  }

  /**
   * Screen-space LOD thresholds, in metres from the camera. Chosen from the actual
   * subject size: at 12 m a 1.8 m soldier is ~400 px tall in a 44 deg frame and the
   * fingers and pouch flaps still read; at 20 m he is ~240 px and his head is 32 px,
   * which is where folding skin into the cloth material stops being visible.
   */
  const LOD_NEAR = 12, LOD_FAR = 20;
  let lodStats = [0, 0, 0];

  /* ---------------------------------------------------------------- *
   * spawn
   * ---------------------------------------------------------------- */
  function spawn(opts = {}) {
    const seed = (opts.seed ?? (0x9e37 + idSeq * 2654435761)) >>> 0;
    // the AI team passes `variant` as an integer; the contract form is a name
    let variant = opts.variant;
    let index = opts.index;
    if (typeof variant === 'number') { index = variant; variant = undefined; }
    const L = opts.loadout || makeLoadout(seed, { variant, index });
    L.scale = Math.round((opts.scale ?? L.scale) * 50) / 50;

    const rig = createRig(L.scale);
    const entry = geometryFor(L, rig);
    entry.refs++;
    const mats = materialsFor(L);

    const root = new THREE.Group();
    root.name = `soldier_${idSeq}`;
    if (opts.position) root.position.copy(opts.position);
    root.rotation.y = opts.yaw ?? 0;

    root.add(rig.rootBone);

    const mkMesh = (geometry, materials) => {
      const m = new THREE.SkinnedMesh(geometry, materials);
      m.castShadow = true;
      m.receiveShadow = true;
      m.userData.surface = 'flesh';
      m.userData.physicsMask = 4;
      m.frustumCulled = true;
      root.add(m);
      m.bind(rig.skeleton);
      return m;
    };
    const mesh = mkMesh(entry.geometry, mats);
    mesh.name = root.name + '_mesh';
    // LOD1 keeps skin and cloth apart; LOD2 folds them together and draws once.
    const lodMeshes = entry.lods.map((e, i) => {
      const m = mkMesh(e.geometry, [mats[0], mats[1]]);
      m.name = `${root.name}_lod${i + 1}`;
      m.visible = false;
      return m;
    });

    const weaponAttach = new THREE.Object3D();
    weaponAttach.name = 'weaponAttach';
    if (entry.gear.weaponGrip) {
      weaponAttach.position.copy(entry.gear.weaponGrip).sub(rig.bind.handR);
    }
    rig.byName.get('handR').add(weaponAttach);

    const rng = makeRng(seed ^ 0x5bf03635);
    const anim = new Animator(rig, root, {
      physics: ctxRef?.physics || null,
      gear: entry.gear,
      rng,
      phase: opts.phase ?? rng(),
    });
    const secondary = new Secondary(rig, L);

    const character = {
      id: idSeq++,
      root, mesh, rig, skeleton: rig.skeleton, loadout: L, anim, secondary,
      position: root.position, rotation: root.rotation, quaternion: root.quaternion,
      weaponAttach,
      health: opts.health ?? 100, alive: true, ragdoll: null,
      drawCalls: entry.draws, triangles: entry.tris,
      visible: true, lod: 0, lodMeshes,
      _entry: entry,

      /** 0 = full mesh, 1 = reduced 2-group, 2 = reduced 1-group. */
      setLod(level) {
        if (character.lod === level) return character;
        character.lod = level;
        mesh.visible = level === 0;
        lodMeshes[0].visible = level === 1;
        lodMeshes[1].visible = level === 2;
        character.drawCalls = level === 0 ? entry.draws : entry.lods[level - 1].draws;
        character.triangles = level === 0 ? entry.tris : entry.lods[level - 1].tris;
        return character;
      },

      setLocomotion(s) { anim.setLocomotion(s); return character; },
      setAimTarget(v) { anim.setAimTarget(v); return character; },
      setLookTarget(v) { anim.setLookTarget(v); return character; },
      play(name, o) { anim.play(name, o || {}); return character; },
      /**
       * Show/hide the whole character.
       *
       * This has to re-assert the WHOLE subtree, not just `root.visible`. The capture
       * harness restores boot-time visibility before every shot and hides everything
       * added to the world scene since boot — which is every character, because they are
       * spawned lazily by the shot that first needs them. That reset walks the tree and
       * clears `visible` on the skinned meshes as well as on the root, so a later shot
       * re-using a pooled character got `root.visible = true` over three hidden meshes and
       * rendered nothing. That is exactly `char_closeup`: 1 character found, 0.00 % of the
       * frame covered, while `char_hero` — which spawns the same soldier fresh — passed.
       *
       * Re-showing has to respect the LOD, or turning a character back on draws the full
       * mesh and both reduced meshes on top of each other.
       */
      setVisible(on) {
        character.visible = !!on;
        root.visible = !!on;
        if (on) {
          root.traverse((o) => { o.visible = true; });
          mesh.visible = character.lod === 0;
          lodMeshes[0].visible = character.lod === 1;
          lodMeshes[1].visible = character.lod === 2;
        }
        return character;
      },

      getBone(name) { return rig.byName.get(name) || null; },
      boneWorldPosition(name, out = new THREE.Vector3()) {
        const b = rig.byName.get(name);
        if (!b) return null;
        return b.getWorldPosition(out);
      },
      /** World transform of the weapon the character is holding. */
      weaponTransform(outPos, outQuat) {
        if (outPos) outPos.copy(anim.weaponPos);
        if (outQuat) outQuat.copy(anim.weaponQuat);
        return character;
      },

      hit(region, impulse, point) {
        if (!character.alive) return character;
        anim.hit(region || 'torso', impulse, point);
        const fx = ctxRef?.fx;
        if (fx && fx.blood && point) {
          const n = impulse ? _v.copy(impulse).normalize().negate() : _v.set(0, 0, 1);
          try { fx.blood(point, n, { amount: region === 'head' ? 1.6 : 1 }); } catch (e) { /* fx optional */ }
        }
        return character;
      },

      /**
       * Blended death. The ragdoll is built from the CURRENT animated pose, the killing
       * impulse goes into the bone that was actually hit, and the skinned mesh blends
       * from animation to ragdoll transforms over 120 ms — so the body falls with the
       * right direction and momentum instead of snapping limp.
       */
      die(impulse, boneName) {
        if (!character.alive) return character;
        character.alive = false;
        const phys = ctxRef?.physics;
        if (!phys || !phys.makeRagdoll) { anim.dead = true; return character; }
        rig.rootBone.updateMatrixWorld(true);
        const vel = anim.velocity.clone().multiplyScalar(0.85);
        const spec = buildRagdollSpec(rig, {
          velocity: [vel.x, vel.y, vel.z],
          seed: (seed ^ 0x1a2b3c) >>> 0,
          mask: phys.MASK ? phys.MASK.WORLD : 1,
          angularKick: 0.5,
        });
        let rd = null;
        try { rd = phys.makeRagdoll(spec); } catch (e) { rd = null; }
        if (!rd) { anim.dead = true; return character; }
        character.ragdoll = rd;
        if (impulse) {
          const rb = REGION_RAGDOLL_BONE[boneName] || (rd.bone(boneName) ? boneName : 'spine');
          try { rd.applyImpulse(rb, impulse); } catch (e) { /* bone name may be unknown */ }
        }
        anim.attachRagdoll(rd, RAGDOLL_DRIVE);
        return character;
      },

      revive() {
        if (character.ragdoll) { try { character.ragdoll.dispose(); } catch (e) { /* gone */ } }
        character.ragdoll = null;
        character.alive = true;
        anim.dead = false;
        anim.ragdoll = null;
        anim.ragdollBlend = 0;
        for (const f of [anim.feet.L, anim.feet.R]) f.plantValid = false;
        secondary.reset();
        return character;
      },

      dispose() {
        const i = characters.indexOf(character);
        if (i >= 0) characters.splice(i, 1);
        if (character.ragdoll) { try { character.ragdoll.dispose(); } catch (e) { /* gone */ } }
        root.removeFromParent();
        entry.refs--;
        // geometry and materials are shared and cached — never disposed per character
      },
    };

    // hit-region helper for the AI team: bone name -> region enum
    character.regionOf = (bone) => {
      for (const r in REGION_BONE) if (REGION_BONE[r] === bone) return r;
      return 'torso';
    };

    (opts.parent || ctxRef.scene).add(root);
    characters.push(character);

    // settle the pose so the very first rendered frame is never a bind/T pose
    anim.update(1 / 60);
    secondary.update(1 / 60, root);
    return character;
  }

  /* ---------------------------------------------------------------- *
   * showcase stage
   * ---------------------------------------------------------------- */
  function buildStage(ctx) {
    if (stage) return stage;
    const mat = (name, opts, fb) => {
      try {
        const m = ctx.materials ? ctx.materials.get(name, opts) : null;
        if (m) return m;
      } catch (e) { /* fall through to the stand-in */ }
      return new THREE.MeshStandardMaterial(fb);
    };

    const g = new THREE.Group();
    g.name = 'character-stage';
    g.position.copy(STAGE);

    const add = (geo, m, x, y, z, ry = 0, surface = 'concrete') => {
      const mesh = new THREE.Mesh(geo, m);
      mesh.position.set(x, y, z);
      mesh.rotation.y = ry;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.surface = surface;
      g.add(mesh);
      return mesh;
    };

    // The sun in this build comes from world -X at ~16° elevation, so the stage is laid
    // out for a camera on -Z: the key rakes across the subject, the backdrop sits behind
    // it at +Z, and the shadow-side wall at +X gives the lit edge something dark to
    // read against. Nothing here is decorative — it is all value structure.
    const ground = mat('asphalt', { worldSize: 8 }, { color: 0x4a4744, roughness: 0.95 });
    const wall = mat('concrete_wall', { worldSize: 6 }, { color: 0x8a8578, roughness: 0.9 });
    const step = mat('concrete_floor', { worldSize: 2 }, { color: 0x9a958a, roughness: 0.92 });
    const plaster = mat('plaster_wall', { worldSize: 6 }, { color: 0x9c9484, roughness: 0.9 });

    add(new THREE.BoxGeometry(52, 1, 62), ground, 0, -0.5, 6, 0, 'asphalt');
    // backdrop, set well back so it stays out of focus and reads as a value, not detail
    add(new THREE.BoxGeometry(62, 7.0, 0.8), plaster, 2.0, 3.5, 31.0);
    add(new THREE.BoxGeometry(16, 4.6, 0.7), plaster, -13.0, 2.3, 11.5, 0.22);
    add(new THREE.BoxGeometry(0.8, 6.0, 26), wall, 13.5, 3.0, 8.0);
    add(new THREE.BoxGeometry(0.7, 3.4, 9), wall, -13.5, 1.7, 6.5);
    // clutter for scale reference and a foreground/midground/background read
    const crate = mat('wood_crate', { worldSize: 1.2 }, { color: 0x8a7250, roughness: 0.9 });
    add(new THREE.BoxGeometry(1.15, 0.92, 1.15), crate, -4.6, 0.46, 5.2, 0.30, 'wood');
    add(new THREE.BoxGeometry(1.15, 0.92, 1.15), crate, -4.1, 1.38, 5.5, 0.11, 'wood');
    add(new THREE.BoxGeometry(1.15, 0.92, 1.15), crate, 5.4, 0.46, 7.1, -0.22, 'wood');
    const barrel = mat('metal_rusted', { worldSize: 1.4 }, { color: 0x6a5b4a, roughness: 0.8, metalness: 0.4 });
    add(new THREE.CylinderGeometry(0.30, 0.30, 0.88, 16), barrel, -6.0, 0.44, 7.4, 0, 'metal');
    add(new THREE.CylinderGeometry(0.30, 0.30, 0.88, 16), barrel, 6.6, 0.44, 4.6, 0, 'metal');

    // stair flight: the ragdoll settles on it and the foot IK has real treads to plant on
    const steps = 9, rise = 0.19, run = 0.31;
    for (let i = 0; i < steps; i++) {
      // full-height blocks, not floating treads — a stair with a gap under the nosing
      // is the kind of thing a hostile art director spots in half a second
      add(new THREE.BoxGeometry(2.8, rise * (i + 1), run), step,
        8.6, rise * (i + 1) * 0.5, 0.4 + i * run, 0, 'concrete');
    }
    add(new THREE.BoxGeometry(2.8, steps * rise, 2.2), step, 8.6, steps * rise * 0.5, 0.4 + steps * run + 1.0);
    // a shallow ramp; the low end is buried so there is never a gap at the join
    const ramp = add(new THREE.BoxGeometry(4.4, 0.62, 5.4), step, -8.2, 0.10, 4.0, 0, 'concrete');
    ramp.rotation.x = 0.155;

    g.updateMatrixWorld(true);
    ctx.scene.add(g);
    stage = g;
    if (ctx.physics && ctx.physics.addStatic) {
      try { ctx.physics.addStatic(g, { mask: ctx.physics.MASK.WORLD }); } catch (e) { /* optional */ }
    }
    return g;
  }

  /* ---------------------------------------------------------------- *
   * shots
   * ---------------------------------------------------------------- */
  function shotCam(ctx, from, at, fov = 42) {
    look(ctx, from, at, fov);
    const dx = at[0] - from[0], dy = at[1] - from[1], dz = at[2] - from[2];
    const yaw = Math.atan2(-dx, -dz);
    const pitch = Math.atan2(dy, Math.hypot(dx, dz));
    shotCamPos.set(from[0], from[1], from[2]);
    shotCamActive = true;
    const P = ctx.get?.('player');
    if (!P) return;
    try {
      if (typeof P.place === 'function') {
        const eyeH = P.rig?.eyeHeight ?? 1.65;
        P.place(new THREE.Vector3(from[0], from[1] - eyeH, from[2]), yaw, pitch);
      }
    } catch (e) { /* freeze + pin below is what matters */ }
    try { if (typeof P.freeze === 'function') P.freeze(true); } catch (e) { /* noop */ }
    try {
      if (P.rig) {
        P.rig.eye.set(from[0], from[1], from[2]);
        P.rig.fov = fov;
        P.rig.quaternion.setFromEuler(_euler.set(pitch, yaw, 0, 'YXZ'));
        P.rig.applyTo(ctx);
      }
    } catch (e) { /* look() above is the fallback */ }
  }

  /* ------------------------------------------------------------------ *
   * subject-relative framing
   *
   * Hard-coded camera coordinates are how a shot ends up photographing a kerb: they
   * describe a place, not a subject, and the moment the subject moves — a ragdoll that
   * settles somewhere new, a head that turns, a stage that gets relocated because the
   * level grew over it — the frame is wrong and nothing says so. These two helpers let a
   * shot say "put the camera where this soldier's face fills the frame" instead.
   * ------------------------------------------------------------------ */

  /**
   * World AABB of a set of characters, measured from LIVE bone positions.
   *
   * Not `Box3.setFromObject`: for a `SkinnedMesh` that returns the bind-pose box under
   * the root transform, which is wrong for anything posed and catastrophically wrong for
   * a ragdoll, whose bones have left the root entirely.
   *
   * @param {object[]} list characters
   * @param {string[]|null} bones subset of bone names, or null for the body bones
   * @param {number} pad metres of flesh/gear to allow around the joint centres
   */
  function subjectBox(list, bones = null, pad = 0.14) {
    const box = new THREE.Box3().makeEmpty();
    for (const c of list) {
      c.root.updateMatrixWorld(true);
      const names = bones || BODY_BONES;
      for (const n of names) {
        const b = c.rig.byName.get(n);
        if (b) box.expandByPoint(b.getWorldPosition(_v));
      }
    }
    if (box.isEmpty()) return null;
    return box.expandByScalar(pad * (list[0]?.rig?.scale ?? 1));
  }

  /**
   * Point the shot camera at `box` from direction `dir`, at whatever distance makes the
   * box fill `fill` of the frame. `fill` is a fraction of the frame's smaller angular
   * axis: 1.0 exactly fits, above 1.0 crops.
   *
   * @param {THREE.Box3} box
   * @param {THREE.Vector3} dir unit vector FROM the subject TOWARD the camera
   */
  function frameBox(ctx, box, { dir, fov = 40, fill = 0.88, lift = 0, aimAt = null } = {}) {
    const centre = box.getCenter(new THREE.Vector3());
    const d = dir.clone().normalize();
    const right = new THREE.Vector3().crossVectors(UP_Y, d);
    if (right.lengthSq() < 1e-6) right.set(1, 0, 0);
    right.normalize();
    const up = new THREE.Vector3().crossVectors(d, right).normalize();

    /**
     * Solve per CORNER rather than from the box's half-extents, because a corner near
     * the camera subtends a bigger angle than the same offset at the box centre. For a
     * corner at depth `p` toward the camera and screen offset `h`, fitting it needs
     * `h / (dist - p) <= fill * tan(fov/2)`, i.e. `dist >= h / (fill*tv) + p`. Taking the
     * max over all eight is exact; the earlier version added the whole box depth to a
     * centre-based estimate and pushed a 13 m-wide subject 3 m further back than it
     * needed, which is how `char_squad` lost two thirds of its coverage to a "framing fix".
     */
    const aspect = ctx.camera?.aspect || 16 / 9;
    const tv = Math.tan((fov * Math.PI) / 360);
    const min = box.min, max = box.max;
    let dist = 0;
    for (let i = 0; i < 8; i++) {
      _v.set(i & 1 ? max.x : min.x, i & 2 ? max.y : min.y, i & 4 ? max.z : min.z).sub(centre);
      const p = _v.dot(d);
      dist = Math.max(dist,
        Math.abs(_v.dot(up)) / (fill * tv) + p,
        Math.abs(_v.dot(right)) / (fill * tv * aspect) + p);
    }

    const look = aimAt ? aimAt.clone() : centre.clone();
    look.y += lift;
    const from = centre.clone().addScaledVector(d, dist);
    from.y += lift;
    shotCam(ctx, [from.x, from.y, from.z], [look.x, look.y, look.z], fov);
    return { from, look, dist };
  }

  /** Unit world direction a bone's +Z (the rig's forward) points in. */
  function boneForward(c, name, out = new THREE.Vector3()) {
    const b = c.rig.byName.get(name);
    if (!b) return out.set(Math.sin(c.root.rotation.y), 0, Math.cos(c.root.rotation.y));
    c.root.updateMatrixWorld(true);
    b.getWorldQuaternion(_q);
    return out.set(0, 0, 1).applyQuaternion(_q);
  }

  /** Pool of characters, created on demand and re-used across shots. */
  const pool = new Map();
  function group(name, make) {
    let g = pool.get(name);
    if (!g) { g = make(); pool.set(name, g); }
    return g;
  }
  function soloShow(names) {
    for (const [k, list] of pool) {
      const on = names.indexOf(k) >= 0;
      for (const c of list) c.setVisible(on);
    }
  }

  /** Deterministically re-settle a character: reset state, then run N frames. */
  function settle(c, frames, dt = 1 / 60) {
    c.anim.feet.L.plantValid = false;
    c.anim.feet.R.plantValid = false;
    c.anim.hipDrop = 0;
    c.secondary.reset();
    for (let i = 0; i < frames; i++) {
      c.anim.update(dt);
      c.secondary.update(dt, c.root);
    }
  }

  /**
   * Shot geometry, all in stage-local metres:
   *   camera lives at -Z, subjects stand near the origin facing roughly -Z,
   *   the sun rakes in from -X. A subject turned to yaw ≈ -2.6 rad therefore shows the
   *   camera its front three-quarter AND puts that same side into the key.
   */
  function registerShots(ctx) {
    const S = STAGE;
    const at = (x, z) => new THREE.Vector3(S.x + x, S.y, S.z + z);
    const pt = (x, y, z) => new THREE.Vector3(S.x + x, y, S.z + z);
    const cam = (c, from, look, fov) => shotCam(c,
      [S.x + from[0], from[1], S.z + from[2]], [S.x + look[0], look[1], S.z + look[2]], fov);
    const FACE = -2.26;   // three-quarter toward a -Z camera, lit side to the lens

    const heroGroup = () => {
      const [a] = heroLoadouts();
      return [spawn({ loadout: a, position: at(0, 0), yaw: FACE, seed: 0x51ce01 })];
    };

    registerShot('char_hero', (c) => {
      const [hero] = group('hero', heroGroup);
      soloShow(['hero']);
      hero.revive();
      hero.root.position.copy(at(0, 0));
      hero.root.rotation.y = FACE;
      hero.setLocomotion({ velocity: new THREE.Vector3(0, 0, 0), grounded: true, crouched: false, stance: 'stand' });
      hero.setAimTarget(pt(-4.4, 1.42, -6.8));
      hero.setLookTarget(pt(-5.6, 1.70, -4.6));
      hero.play('uncover');
      settle(hero, 100);
      cam(c, [-0.58, 1.16, -2.78], [-0.04, 0.99, 0], 36);
    }, { settle: 12, desc: 'hero soldier, three-quarter view — gear and silhouette' });

    registerShot('char_closeup', (c) => {
      const [hero] = group('hero', heroGroup);
      soloShow(['hero']);
      hero.revive();
      hero.root.position.copy(at(0, 0));
      hero.root.rotation.y = FACE;
      hero.setLocomotion({ velocity: new THREE.Vector3(0, 0, 0), grounded: true });
      // Look and aim BOTH forward-left of the body's facing, so the head stays inside
      // its own yaw limit instead of being cranked round to the aim target. The old
      // targets sat 120 deg off the body and the shot photographed the back of a helmet.
      hero.setAimTarget(pt(-3.6, 1.46, -6.4));
      hero.setLookTarget(pt(-3.9, 1.62, -6.9));
      settle(hero, 100);
      /**
       * Framed off the HEAD, not off stage coordinates.
       *
       * The camera direction is derived from where the head is actually pointing after
       * the settle — three-quarter to the face side, slightly above the eyeline — so the
       * shot cannot end up behind the skull no matter what the aim layer does to the neck.
       * Shooting from below the eyes puts the helmet brim across the eye sockets in
       * projection, so the lift is positive and small.
       *
       * `fill` is well above 1, i.e. it deliberately CROPS. A closeup that merely fits
       * head and shoulders inside a 16:9 frame leaves two thirds of the frame as
       * background — measured, that framing covers 15 % of the frame with subject, and
       * `wpn_closeup` covers 72 %. Cropping the shoulders and holding the head across most
       * of the frame height takes it to ~30 %, which is what a closeup is for.
       */
      const fwd = boneForward(hero, 'head', new THREE.Vector3());
      fwd.y = 0;
      if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, 1);
      fwd.normalize().applyAxisAngle(UP_Y, 0.42);      // 24 deg off-axis: a three-quarter
      fwd.y = 0.14;                                     // just above the eyeline
      const box = subjectBox([hero], ['neck', 'head', 'headEnd', 'clavicleL', 'clavicleR',
        'upperArmL', 'upperArmR'], 0.12);
      // aim at the head, so what falls outside the crop is chest and not helmet
      const eyeAt = hero.boneWorldPosition('head', new THREE.Vector3());
      frameBox(c, box, { dir: fwd, fov: 34, fill: 1.5, aimAt: eyeAt });
    }, { settle: 12, desc: 'head and shoulders — helmet, headset, fabric, skin' });

    registerShot('char_variants', (c) => {
      const g = group('variants', () => heroLoadouts().map((l, i) => spawn({
        loadout: l, position: at((i - 1) * 1.16, 0), yaw: FACE, seed: 0x51ce01 + i,
      })));
      soloShow(['variants']);
      g.forEach((ch, i) => {
        ch.revive();
        ch.root.position.copy(at((i - 1) * 1.16, i * 0.30));
        ch.root.rotation.y = FACE + (i - 1) * 0.30;
        // three different postures — a variants sheet of one pose three times proves
        // nothing about the rig
        if (i === 0) { ch.play('lowReady'); ch.setLocomotion({ velocity: new THREE.Vector3(0, 0, 0), grounded: true }); }
        else if (i === 1) { ch.setLocomotion({ velocity: new THREE.Vector3(0, 0, 0), grounded: true, crouched: true, stance: 'crouch' }); }
        else { ch.play('uncover'); ch.setLocomotion({ velocity: new THREE.Vector3(0, 0, 0), grounded: true }); }
        ch.setAimTarget(pt(-5.4 + i * 1.6, 1.40 - i * 0.06, -7.5));
        ch.setLookTarget(pt(-4.0 + (i - 1) * 2.2, 1.56, -5.2));
        settle(ch, 90);
      });
      cam(c, [0.05, 1.22, -4.15], [0.05, 0.98, 0.22], 33);
    }, { settle: 12, desc: 'three seeded loadout variants side by side' });

    registerShot('char_locomotion', (c) => {
      const g = group('run', () => [spawn({
        seed: 0x7a1103, variant: 'assault', position: at(-5.5, 1.0), yaw: 1.35,
      })]);
      const r = g[0];
      soloShow(['run']);
      r.revive();
      const YAW = -1.86;                            // running toward -X, lit side to camera
      r.root.position.copy(at(7.0, 4.6));
      r.root.rotation.y = YAW;
      r.anim.phase = 0;
      // 3.6 m/s is the RUN gait. The old 4.7 tipped over the 4.0 sprint threshold, and
      // the sprint layer drops the weapon out of the shoulder and points it at the
      // ground — so a shot titled "mid-run" was showing a sprint with the rifle stowed.
      const v = new THREE.Vector3(Math.sin(YAW), 0, Math.cos(YAW)).multiplyScalar(3.6);
      r.setLocomotion({ velocity: v, grounded: true, crouched: false, stance: 'run' });
      r.setAimTarget(pt(-14, 1.30, 2.0));
      r.setLookTarget(pt(-14, 1.45, 2.0));
      // run it forward for real, so every plant in the shot is a genuine one
      for (let i = 0; i < 142; i++) {
        r.anim.update(1 / 60);
        r.secondary.update(1 / 60, r.root);
        r.root.position.addScaledVector(v, 1 / 60);
      }
      const p = r.root.position;
      shotCam(c, [p.x - 1.45, 1.18, p.z - 2.65], [p.x + 0.10, 0.94, p.z + 0.20], 40);
    }, { settle: 4, desc: 'mid-run, feet planted, real stride' });

    registerShot('char_aim', (c) => {
      const g = group('aim', () => [spawn({
        seed: 0x2f9d41, variant: 'breacher', position: at(0.3, 0.2), yaw: 0,
      })]);
      const a = g[0];
      soloShow(['aim']);
      a.revive();
      a.root.position.copy(at(0.25, 0.15));
      // Feet and hips point one way, the weapon another: 65 degrees of separation is
      // what makes the spine twist readable instead of a whole-body turn.
      a.root.rotation.y = Math.PI - 0.28;
      a.setLocomotion({ velocity: new THREE.Vector3(0, 0, 0), grounded: true });
      a.setAimTarget(pt(-6.6, 1.30, -2.2));
      a.setLookTarget(pt(-6.6, 1.36, -2.2));
      settle(a, 120);
      a.play('fire');
      for (let i = 0; i < 3; i++) { a.anim.update(1 / 60); a.secondary.update(1 / 60, a.root); }
      /**
       * Frame off where the man is LOOKING, not off where his hips point.
       *
       * The whole point of this shot is that the two disagree by 65 degrees, and a camera
       * placed in front of the hips lands behind the shoulders: it photographs a back
       * panel and the rear of a helmet. The old fixed coordinates did exactly that, and so
       * did a first attempt that used the chest — the head carries another 40 degrees on
       * top of the chest, so anything that ignores it still ends up behind the skull.
       * Taking the head's forward and swinging 30 degrees off it puts the lens on the
       * weapon side of the twist, where the hips-versus-shoulders separation IS the
       * silhouette and the face is still readable.
       */
      const face = boneForward(a, 'head', new THREE.Vector3());
      face.y = 0;
      if (face.lengthSq() < 1e-6) face.set(0, 0, 1);
      // +0.52 rad, tried against -0.30 and +0.30: at 0.30 the lens ends up nearly on the
      // aim axis and the weapon disappears behind the man's own shoulder; at 0.52 the
      // carrier, both hands and the muzzle are all in silhouette. `fill` stays at 1.0 —
      // cropping a standing figure crops the helmet and the boots equally, which is only
      // ever right for a closeup.
      face.normalize().applyAxisAngle(UP_Y, 0.52);
      face.y = 0.13;
      frameBox(c, subjectBox([a], null, 0.13), { dir: face, fov: 38, fill: 1.0 });
    }, { settle: 6, desc: 'aiming off to one side — spine twist, both hands on the weapon' });

    registerShot('char_ragdoll', (c) => {
      const g = group('dead', () => [spawn({
        seed: 0x6d0a55, variant: 'assault', position: at(8.6, 1.9), yaw: 0,
      })]);
      const d = g[0];
      soloShow(['dead']);
      d.revive();
      /**
       * Standing on the FIFTH tread, shot from the front — he falls back down the flight.
       *
       * The treads are full-height blocks: step i spans z in [0.4 + 0.31i, 0.4 + 0.31(i+1)]
       * with its top at 0.19(i+1). The old placement (z = 1.95, y = 0.95) sat exactly on
       * the boundary between step 4 and step 5 with its top at 1.14 — so the soldier stood
       * 19 cm INSIDE the stair. `buildRagdollSpec` samples the current pose, so the whole
       * ragdoll spawned inside solid geometry and depenetration flushed it under the
       * flight: the old char_ragdoll frame is a staircase with two knees sticking out.
       */
      // Step 7 of 9 (block tops are 0.19*(i+1), treads span z 0.4+0.31i .. +0.31), so he
      // has seven treads under him to fall down instead of one.
      d.root.position.set(S.x + 8.6, S.y + 0.19 * 8, S.z + 2.72);
      d.root.rotation.y = Math.PI;
      d.setLocomotion({ velocity: new THREE.Vector3(0, 0, 0), grounded: true });
      d.setAimTarget(pt(8.6, 1.5, -6));
      settle(d, 40);
      const P = ctx.physics;
      if (P) {
        const probe = P.raycast(pt(8.6, S.y + 4, 1.78), new THREE.Vector3(0, -1, 0), 8, P.MASK.WORLD);
        if (!probe) { try { P.rebuild(); } catch (e) { /* the BVH may not be dirty */ } }
      }
      /**
       * Almost pure -Z, i.e. straight down the flight.
       *
       * The old impulse carried -5 in X. The flight is only 2.8 m wide and he stands on
       * its centre line, so that component threw him clear of the treads entirely: he
       * landed on the apron beside the stairs and folded into a ball against the side of
       * the landing block, which is the 1.27 %-coverage frame. Down the stairs is the shot.
       */
      d.die(new THREE.Vector3(-0.8, 2.4, -7.5), 'chest');
      // let the 120 ms animation->ragdoll blend play, then settle the body without
      // paying for four hundred rendered frames
      for (let i = 0; i < 26; i++) { d.anim.update(1 / 60); d.secondary.update(1 / 60, d.root); }
      if (d.ragdoll) for (let i = 0; i < 520; i++) d.ragdoll.step(1 / 120);
      for (let i = 0; i < 8; i++) { d.anim.update(1 / 60); d.secondary.update(1 / 60, d.root); }
      /**
       * Frame the body where it ACTUALLY ended up, at whatever distance fills the frame.
       *
       * The old version pointed at the chest bone from a fixed 4.6 m offset. A ragdoll
       * does not settle in a fixed place — this one came to rest against the side of the
       * flight and measured **1.27 % subject coverage** in a frame that was 80 % empty
       * apron. Measuring the settled body's own bounding box and backing off exactly far
       * enough is the only version of this that survives the body landing somewhere else.
       */
      const body = subjectBox([d], null, 0.16);
      // Down the flight and across it: the stairs read as the thing he fell down only if
      // some of them are in shot, so the lens sits low and off the fall line.
      const dir = new THREE.Vector3(-0.66, 0.24, -0.71).normalize();
      frameBox(c, body, { dir, fov: 42, fill: 0.80 });
    }, { settle: 10, desc: 'blended death — body settled on the stairs' });

    registerShot('char_squad', (c) => {
      const g = group('squad', () => {
        const lay = [
          [-2.2, 1.2, -2.55], [1.5, 2.4, -3.00], [4.2, 1.0, -2.15],
          [-5.0, 5.6, -2.70], [2.4, 6.8, -2.40], [7.0, 4.6, -3.10],
          [-8.2, 11.0, -2.60], [3.4, 12.4, -2.90], [9.6, 9.4, -2.30],
          [-6.0, 17.5, -2.70], [1.2, 19.5, -2.50], [8.4, 16.0, -3.00],
        ];
        return lay.map(([x, z, yaw], i) => spawn({
          seed: 0xa10000 + i * 7919, index: i, position: at(x, z), yaw,
          phase: (i * 0.37) % 1,
        }));
      });
      soloShow(['squad']);
      g.forEach((ch, i) => {
        ch.revive();
        const yaw = ch.root.rotation.y;
        const mode = i % 3;
        const v = mode === 0
          ? new THREE.Vector3()
          : new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(mode === 1 ? 1.5 : 3.8);
        const crouch = i === 1 || i === 4 || i === 8 || i === 10;
        ch.setLocomotion({ velocity: v, grounded: true, crouched: crouch, stance: crouch ? 'crouch' : 'stand' });
        // Twelve identical shoulder-fired poses is a lineup, not a squad. Rotate the
        // upper-body layers so the silhouettes differ at a glance: weapons up, weapons
        // down, and men leaned out around cover.
        if (i % 5 === 0) ch.play('lowReady');
        else if (i % 5 === 2) ch.play('peek', { side: i % 2 ? 'left' : 'right' });
        else ch.play('uncover');
        // Fan the aim so they are not all covering the same doorway.
        const spread = -6 + i * 1.15;
        ch.setAimTarget(pt(spread, 1.42 + (i % 3) * 0.07, -14 + (i % 4) * 2.5));
        ch.setLookTarget(pt(spread + (i % 3 - 1) * 2.0, 1.58, -13));
        settle(ch, 70 + i * 3);
      });
      /**
       * Hand-placed, and deliberately NOT `frameBox`.
       *
       * Auto-framing wants to fit the whole subject box, and this subject is 13 m wide
       * and 19 m deep — fitting it puts the lens 13 m back and turns every soldier into a
       * 60-pixel smudge (measured: 1.60 % coverage against 4.67 % hand-placed). A crowd
       * shot is composed by choosing what to cut off, which is a judgement, not a fit.
       * The lens sits 2.7 m short of the near man so he anchors the foreground, and the
       * back of the squad is allowed to run out of frame.
       */
      cam(c, [-1.35, 1.56, -2.7], [0.55, 1.15, 9.0], 42);
    }, { settle: 10, desc: 'twelve soldiers at varied distances — variety and the perf budget' });
  }

  /* ---------------------------------------------------------------- *
   * the System
   * ---------------------------------------------------------------- */
  return {
    name: 'characters',
    priority: 72,

    init(ctx) {
      ctxRef = ctx;
      buildStage(ctx);

      const api = {
        spawn,
        get all() { return characters.slice(); },
        get count() { return characters.length; },
        stage: STAGE.clone(),
        /** Per-character cost, for the perf budget. */
        stats() {
          const rows = [...geoCache.entries()].map(([k, e]) => ({
            shape: k.split('|')[0], tris: e.tris, draws: e.draws, refs: e.refs,
            lodTris: e.lods.map((l) => l.tris), lodDraws: e.lods.map((l) => l.draws),
          }));
          const vis = characters.filter((c) => c.visible);
          return {
            characters: characters.length,
            alive: characters.filter((c) => c.alive).length,
            shapes: geoCache.size,
            materials: matCache.size * 4,
            perCharacter: rows,
            maxTris: rows.reduce((m, r) => Math.max(m, r.tris), 0),
            maxDraws: rows.reduce((m, r) => Math.max(m, r.draws), 0),
            lod: lodStats.slice(),
            lodNear: lodStats[0], lodMid: lodStats[1], lodFar: lodStats[2],
            // groups actually submitted per pass, summed over the visible squad
            groupsPerPass: vis.reduce((n, c) => n + c.drawCalls, 0),
          };
        },
        dispose(c) { if (c && c.dispose) c.dispose(); },
      };
      ctx.characters = api;

      // FX / weapons may report hits straight at us; the AI team normally routes these,
      // but tolerating the raw event means a hit still reads even before AI lands.
      ctx.events.on('ballistics:hit', (e) => {
        if (!e || !e.entity || !e.entity.__character) return;
        const ch = e.entity.__character;
        if (ch.hit) ch.hit(e.region || 'torso', e.impulse, e.point);
      });

      registerShots(ctx);
    },

    update(dt, ctx) {
      lodStats = [0, 0, 0];
      const cam = ctx.camera.position;
      for (const c of characters) {
        if (!c.visible) continue;
        c.anim.update(dt);
        c.secondary.update(dt, c.root);
        // Hysteresis-free but stable: the thresholds are 8 m apart, and a soldier
        // crossing one changes 2 draw calls, not his appearance.
        const d = c.root.position.distanceTo(cam);
        c.setLod(d < LOD_NEAR ? 0 : d < LOD_FAR ? 1 : 2);
        lodStats[c.lod]++;
      }
      // While one of our own shots owns the camera the first-person viewmodel would
      // cover a quarter of the frame; hide it until anything else moves the camera.
      if (shotCamActive) {
        const mine = ctx.camera.position.distanceToSquared(shotCamPos) < 1e-4;
        ctx.viewScene.visible = !mine;
        if (!mine) shotCamActive = false;
      }
    },

    dispose() {
      for (const c of characters.slice()) c.dispose();
      for (const [, e] of geoCache) {
        e.geometry.dispose();
        for (const l of e.lods) l.geometry.dispose();
      }
      geoCache.clear();
      if (stage) stage.removeFromParent();
    },
  };
}

void _q;
